// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * Review step component for the initial true-up workflow.
 *
 * Displays the balance comparison between GL and Hardfin balances,
 * the onboarding journal entry, and confirmation controls for locking
 * the initial period.
 */
import { lockInitialTrueUp } from '@/accounting/api/lock-initial-true-up';
import {
  InitialTrueUpBalances,
  InitialTrueUpCloseRunResponse,
  JournalEntryResponse,
} from '@/accounting/api/types';
import { CloseRunInfoBar } from '@/accounting/components/close-run-info-bar';
import { formatPeriod } from '@/accounting/utilities/generate-period-list';
import {
  SUPPORT_FALLBACK_ERROR_MESSAGE,
  getUserFacingErrorMessage,
  handleAPIError,
} from '@/api/error-utils';
import { SupportSnackbar } from '@/components/support-snackbar';
import {
  ChevronLeftRounded,
  DownloadRounded,
  LockOutlined,
  LockRounded,
  WarningAmberOutlined,
} from '@/constants/hardfin-standard-icons';
import { IsoCurrencyCode } from '@/currency/constants/iso';
import { CurrencyDecimal } from '@/currency/utilities/decimal';
import { formatAmountWithCurrency } from '@/currency/utilities/format';
import { useDownloadFileByKey } from '@/hooks/use-download-file-by-key';
import { formatNumber } from '@/utilities/number';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { captureException } from '@sentry/react';
import { useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { Decimal } from 'decimal.js';
import { DateTime } from 'luxon';
import { useState } from 'react';

// Variance threshold for material differences (10%)
const MATERIAL_VARIANCE_THRESHOLD = new CurrencyDecimal('0.1');

/**
 * Calculates the percentage variance between GL and Hardfin balances.
 * Returns the absolute percentage difference relative to the Hardfin value.
 * Returns 0 if Hardfin balance is 0 to avoid division by zero.
 */
function calculateVariancePercent(gl: Decimal, hardfin: Decimal): Decimal {
  if (hardfin.isZero()) {
    // 100% variance if Hardfin is 0 but GL is not
    return new CurrencyDecimal(gl.isZero() ? 0 : 1);
  }

  return gl.minus(hardfin).dividedBy(hardfin).abs();
}

/**
 * Checks if any balance has a material variance (>10% of Hardfin value).
 */
function hasMaterialVariance(
  glBalances: InitialTrueUpBalances,
  hardfinBalances: InitialTrueUpBalances
): boolean {
  const inventoryVariance = calculateVariancePercent(
    glBalances.inventory,
    hardfinBalances.inventory
  );
  const fixedAssetsVariance = calculateVariancePercent(
    glBalances.fixedAssets,
    hardfinBalances.fixedAssets
  );
  const accumulatedDeprVariance = calculateVariancePercent(
    glBalances.accumulatedDepreciation,
    hardfinBalances.accumulatedDepreciation
  );

  return (
    inventoryVariance.greaterThan(MATERIAL_VARIANCE_THRESHOLD) ||
    fixedAssetsVariance.greaterThan(MATERIAL_VARIANCE_THRESHOLD) ||
    accumulatedDeprVariance.greaterThan(MATERIAL_VARIANCE_THRESHOLD)
  );
}

const BALANCE_ACCOUNT_LABELS = [
  'Inventory',
  'Fixed assets',
  'Accumulated depreciation',
] as const;

const NOT_LOCKED_PLACEHOLDER = '—';

interface BalanceRow {
  label: string;
  // Null when the currency is not yet trued up (no locked balances exist).
  gl: Decimal | null;
  hardfin: Decimal | null;
  delta: Decimal | null;
}

/**
 * Props for the BalanceComparisonTable component.
 */
type BalanceComparisonTableProps =
  | { currencyCode: IsoCurrencyCode; notLocked: true }
  | {
      currencyCode: IsoCurrencyCode;
      notLocked?: false;
      glBalances: InitialTrueUpBalances;
      hardfinBalances: InitialTrueUpBalances;
      deltas: InitialTrueUpBalances;
    };

/**
 * Component to display the balance comparison table showing GL vs Hardfin balances.
 * Includes variance percentage and highlights material variances (>5%).
 */
export function BalanceComparisonTable(props: BalanceComparisonTableProps) {
  const rows: BalanceRow[] = props.notLocked
    ? BALANCE_ACCOUNT_LABELS.map((label) => ({
        label,
        gl: null,
        hardfin: null,
        delta: null,
      }))
    : [
        {
          label: 'Inventory',
          gl: props.glBalances.inventory,
          hardfin: props.hardfinBalances.inventory,
          delta: props.deltas.inventory,
        },
        {
          label: 'Fixed assets',
          gl: props.glBalances.fixedAssets,
          hardfin: props.hardfinBalances.fixedAssets,
          delta: props.deltas.fixedAssets,
        },
        {
          label: 'Accumulated depreciation',
          gl: props.glBalances.accumulatedDepreciation,
          hardfin: props.hardfinBalances.accumulatedDepreciation,
          delta: props.deltas.accumulatedDepreciation,
        },
      ];

  const currency = props.currencyCode;

  return (
    <TableContainer component={Paper} variant="outlined">
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Account</TableCell>
            <TableCell align="right">General ledger balance</TableCell>
            <TableCell align="right">Hardfin balance</TableCell>
            <TableCell align="right">Delta</TableCell>
            <TableCell align="right">Variance</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => {
            if (row.gl === null || row.hardfin === null || row.delta === null) {
              return (
                <TableRow key={row.label}>
                  <TableCell>{row.label}</TableCell>
                  <TableCell
                    align="right"
                    colSpan={4}
                    sx={{ color: 'text.secondary' }}
                  >
                    {NOT_LOCKED_PLACEHOLDER}
                  </TableCell>
                </TableRow>
              );
            }
            const variancePercent = calculateVariancePercent(
              row.gl,
              row.hardfin
            );
            const isMaterial = variancePercent.greaterThan(
              MATERIAL_VARIANCE_THRESHOLD
            );
            const hasDelta = !row.delta.isZero();
            return (
              <TableRow key={row.label}>
                <TableCell>{row.label}</TableCell>
                <TableCell align="right" sx={{ fontFamily: 'monospace' }}>
                  {formatAmountWithCurrency(row.gl, currency)}
                </TableCell>
                <TableCell align="right" sx={{ fontFamily: 'monospace' }}>
                  {formatAmountWithCurrency(row.hardfin, currency)}
                </TableCell>
                <TableCell
                  align="right"
                  sx={{
                    fontFamily: 'monospace',
                    color: hasDelta ? 'warning.main' : 'text.secondary',
                    fontWeight: hasDelta ? 600 : 400,
                  }}
                >
                  {formatAmountWithCurrency(row.delta, currency)}
                </TableCell>
                <TableCell
                  align="right"
                  sx={{
                    fontFamily: 'monospace',
                    color: isMaterial ? 'error.main' : 'text.secondary',
                    fontWeight: isMaterial ? 600 : 400,
                  }}
                >
                  {formatNumber(variancePercent.times(100).toFixed(1))}%
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

/**
 * Props for the OnboardingJournalEntry component.
 */
interface OnboardingJournalEntryProps {
  journalEntry: JournalEntryResponse;
  /** ISO code the entry is denominated in; amounts render with this code. */
  currencyCode: IsoCurrencyCode;
  /**
   * Whether the period is locked. When true, shows a lock icon.
   */
  isLocked?: boolean;
}

/**
 * Component to display the onboarding journal entry.
 */
export function OnboardingJournalEntry(props: OnboardingJournalEntryProps) {
  const totalDebits = props.journalEntry.totalDebits;
  const totalCredits = props.journalEntry.totalCredits;
  const downloadCsv = useDownloadFileByKey();

  return (
    <Card variant="outlined" sx={{ paddingX: 1 }}>
      <CardHeader
        title="Onboarding journal entry"
        subheader={`Entry date: ${props.journalEntry.date.toLocaleString(DateTime.DATE_FULL)}`}
        sx={{ paddingTop: 3, paddingBottom: 2 }}
        avatar={
          props.isLocked ? (
            <LockOutlined fontSize="small" color="disabled" />
          ) : undefined
        }
        action={
          <Button
            variant="outlined"
            size="small"
            startIcon={
              downloadCsv.isPending ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <DownloadRounded fontSize="small" />
              )
            }
            disabled={!props.journalEntry.csvFileKey || downloadCsv.isPending}
            onClick={() => {
              if (props.journalEntry.csvFileKey) {
                downloadCsv.mutate(props.journalEntry.csvFileKey);
              }
            }}
          >
            Export CSV
          </Button>
        }
      />
      <CardContent sx={{ paddingTop: 0 }}>
        <Stack spacing={2}>
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{ fontFamily: 'monospace', paddingX: 1, paddingTop: 1 }}
          >
            {props.journalEntry.memo}
          </Typography>

          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Account</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell align="right">Debit</TableCell>
                  <TableCell align="right">Credit</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {props.journalEntry.lineItems.map((lineItem) => (
                  <TableRow key={lineItem.id}>
                    <TableCell sx={{ fontFamily: 'monospace' }}>
                      {lineItem.ledgerAccountName}
                    </TableCell>
                    <TableCell>{lineItem.description}</TableCell>
                    <TableCell align="right" sx={{ fontFamily: 'monospace' }}>
                      {new CurrencyDecimal(lineItem.debit).greaterThan(0)
                        ? formatAmountWithCurrency(
                            lineItem.debit,
                            props.currencyCode
                          )
                        : '—'}
                    </TableCell>
                    <TableCell align="right" sx={{ fontFamily: 'monospace' }}>
                      {new CurrencyDecimal(lineItem.credit).greaterThan(0)
                        ? formatAmountWithCurrency(
                            lineItem.credit,
                            props.currencyCode
                          )
                        : '—'}
                    </TableCell>
                  </TableRow>
                ))}
                <TableRow>
                  <TableCell colSpan={2} sx={{ fontWeight: 'bold' }}>
                    Total
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{ fontWeight: 'bold', fontFamily: 'monospace' }}
                  >
                    {formatAmountWithCurrency(totalDebits, props.currencyCode)}
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{ fontWeight: 'bold', fontFamily: 'monospace' }}
                  >
                    {formatAmountWithCurrency(totalCredits, props.currencyCode)}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Stack>
      </CardContent>
    </Card>
  );
}

/**
 * Props for the ReviewStep component.
 */
interface ReviewStepProps {
  closeRun: InitialTrueUpCloseRunResponse;
  onBack: () => void;
  onLockSuccess: () => void;
}

/**
 * Review step component for confirming and locking the initial true-up.
 *
 * Displays the balance comparison, journal entry, and confirmation controls.
 * Handles the lock action with proper error handling and cache invalidation.
 */
export function ReviewStep(props: ReviewStepProps) {
  const queryClient = useQueryClient();
  const downloadPeriodSummary = useDownloadFileByKey();
  const [signature, setSignature] = useState('');
  const [notes, setNotes] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [isLocking, setIsLocking] = useState(false);
  const [showErrorSnackbar, setShowErrorSnackbar] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const periodEndDate = DateTime.fromObject({
    year: props.closeRun.period.year,
    month: props.closeRun.period.month,
  }).endOf('month');

  // Check for material variances
  const showVarianceWarning = hasMaterialVariance(
    props.closeRun.glBalances,
    props.closeRun.hardfinBalances
  );

  const canLock = signature.trim() !== '' && confirmed && !isLocking;

  const handleLock = async () => {
    setIsLocking(true);
    setShowErrorSnackbar(false);
    setErrorMessage(null);

    try {
      await lockInitialTrueUp(props.closeRun.id, signature, notes || undefined);

      // Invalidate queries to refresh data
      await queryClient.invalidateQueries({
        queryKey: ['accounting', 'initial-true-up'],
      });
      // Refetch unmounted caches too, so the next accounting page never picks
      // its default period from a list that predates this lock.
      await queryClient.invalidateQueries({
        queryKey: ['accounting', 'initial-true-ups'],
        refetchType: 'all',
      });
      await queryClient.invalidateQueries({
        queryKey: ['accounting', 'periods'],
        refetchType: 'all',
      });

      props.onLockSuccess();
    } catch (error) {
      const apiError = handleAPIError(error as AxiosError);
      // Reset both surfaces before branching so a retry never leaves
      // stale state from the previous attempt visible alongside the
      // new one.
      setErrorMessage(null);
      setShowErrorSnackbar(false);

      // Backend errors: surface a curated message if one exists, else
      // fall back to the support snackbar. Curated codes are the handled
      // set and skip Sentry; the fallback path fires Sentry so on-call
      // sees unexpected lock failures.
      const curated = getUserFacingErrorMessage(apiError);
      if (curated !== SUPPORT_FALLBACK_ERROR_MESSAGE) {
        setErrorMessage(curated);
      } else {
        if (!apiError.sentToSentry) {
          captureException(error);
        }

        setShowErrorSnackbar(true);
      }
    } finally {
      setIsLocking(false);
    }
  };

  const getTooltipTitle = () => {
    if (!confirmed) {
      return 'Please confirm the checkbox before proceeding';
    }
    if (signature.trim() === '') {
      return 'Please enter your signature before proceeding';
    }
    return '';
  };

  return (
    <Stack spacing={3}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="h6">Review and confirm initial true-up</Typography>
        <Stack direction="row" spacing={2} alignItems="center">
          <Button
            variant="outlined"
            color="primary"
            size="small"
            startIcon={
              downloadPeriodSummary.isPending ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <DownloadRounded fontSize="small" />
              )
            }
            disabled={
              !props.closeRun.excelFileKey || downloadPeriodSummary.isPending
            }
            onClick={() => {
              if (props.closeRun.excelFileKey) {
                downloadPeriodSummary.mutate(props.closeRun.excelFileKey);
              }
            }}
          >
            Download period summary
          </Button>
          <Tooltip arrow title={!canLock ? getTooltipTitle() : ''}>
            <Box>
              <Button
                variant="contained"
                color="secondary"
                size="small"
                startIcon={<LockRounded fontSize="small" />}
                onClick={handleLock}
                disabled={!canLock}
              >
                {isLocking ? 'Locking...' : 'Lock initial period'}
              </Button>
            </Box>
          </Tooltip>
        </Stack>
      </Stack>

      {/* Step guidance */}
      <Alert severity="info">
        <AlertTitle>What happens after lock</AlertTitle>
        <Typography variant="body2" sx={{ mb: 1 }}>
          Hardfin will become the authoritative subledger for your asset
          accounting. Future period closes will use Hardfin's computed balances
          as source of truth.
        </Typography>
        <Typography variant="body2">
          Review the balance comparison below to see how your GL balances
          compare to Hardfin's computed values. The journal entry shows the
          true-up entries you'll need to post to your general ledger to align
          the two systems.
        </Typography>
      </Alert>

      {/* Error message for handled errors */}
      {errorMessage && <Alert severity="error">{errorMessage}</Alert>}

      {/* Balance comparison */}
      <Card variant="outlined" sx={{ paddingX: 1 }}>
        <CardHeader
          title="Balance comparison"
          subheader={`Comparing your GL balances to Hardfin's computed values as of ${periodEndDate.toLocaleString(DateTime.DATE_FULL)}`}
          sx={{ paddingTop: 3, paddingBottom: 0 }}
        />
        <CardContent>
          <Stack spacing={2}>
            {/* Material variance warning */}
            {showVarianceWarning && (
              <Alert severity="warning" icon={<WarningAmberOutlined />}>
                <AlertTitle>Material variance detected</AlertTitle>

                <Typography variant="body2">
                  One or more account balances have a variance greater than{' '}
                  {MATERIAL_VARIANCE_THRESHOLD.times(100).toFixed(0)}% between
                  your GL and Hardfin. This may indicate missing assets,
                  incorrect cost basis entries, or data entry errors. Please
                  verify that all data has been loaded correctly before
                  proceeding.
                </Typography>
              </Alert>
            )}

            <Box>
              <Typography variant="subtitle2" gutterBottom>
                {props.closeRun.currency}
              </Typography>
              <BalanceComparisonTable
                currencyCode={props.closeRun.currency}
                glBalances={props.closeRun.glBalances}
                hardfinBalances={props.closeRun.hardfinBalances}
                deltas={props.closeRun.deltas}
              />
            </Box>
          </Stack>
        </CardContent>
      </Card>

      <OnboardingJournalEntry
        currencyCode={props.closeRun.currency}
        journalEntry={props.closeRun.journalEntry}
      />

      {/* Confirmation section */}
      <Card variant="outlined" sx={{ paddingX: 1 }}>
        <CardHeader
          title="Confirm and lock"
          subheader={formatPeriod(props.closeRun.period)}
          sx={{ paddingTop: 3, paddingBottom: 0 }}
        />
        <CardContent>
          <Stack spacing={2}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={confirmed}
                  onChange={(e) => setConfirmed(e.target.checked)}
                  size="small"
                />
              }
              slotProps={{ typography: { variant: 'body2' } }}
              label={`I confirm that I have reviewed the balance comparison and journal entry, that I have posted the onboarding journal entry to our general ledger, and that I understand locking will establish Hardfin as the authoritative subledger.`}
            />

            <TextField
              size="small"
              label="Signature (required)"
              placeholder="Enter your full name"
              value={signature}
              onChange={(e) => setSignature(e.target.value)}
              fullWidth
              required
            />

            <TextField
              size="small"
              label="Notes (optional)"
              multiline
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              fullWidth
            />

            <Alert severity="warning" icon={<WarningAmberOutlined />}>
              Locking the initial period will{' '}
              <strong>
                establish Hardfin balances as of{' '}
                {periodEndDate.toLocaleString(DateTime.DATE_FULL)}
              </strong>
              . This action cannot be undone.
            </Alert>
          </Stack>
        </CardContent>
      </Card>

      {/* Navigation buttons */}
      <Stack direction="row" justifyContent="space-between">
        <Button
          variant="outlined"
          color="primary"
          size="small"
          startIcon={<ChevronLeftRounded fontSize="small" />}
          onClick={props.onBack}
          disabled={isLocking}
        >
          Back
        </Button>
        <Tooltip arrow title={!canLock ? getTooltipTitle() : ''}>
          <Box>
            <Button
              variant="contained"
              color="secondary"
              size="small"
              startIcon={<LockRounded fontSize="small" />}
              onClick={handleLock}
              disabled={!canLock}
            >
              {isLocking ? 'Locking...' : 'Lock initial period'}
            </Button>
          </Box>
        </Tooltip>
      </Stack>

      {/* Period info */}
      <CloseRunInfoBar
        closeRunKey={props.closeRun.key}
        createdAt={props.closeRun.createdAt}
        period={props.closeRun.period}
        createdBy={props.closeRun.createdBy}
      />

      <SupportSnackbar
        isOpen={showErrorSnackbar}
        onClose={() => setShowErrorSnackbar(false)}
        message="An error occurred while locking the initial period."
      />
    </Stack>
  );
}
